import { getActiveRawMs, getSessionElapsedMs, isRunLedgerReconciled } from './runReducer';
import type { RunState } from './types';
import { RUN_SCHEMA_VERSION } from './types';

export function snapshotRun(state: RunState, nowPerformance: number): RunState {
  return {
    ...state,
    schemaVersion: RUN_SCHEMA_VERSION,
    rawActiveMs: getActiveRawMs(state, nowPerformance),
    activeSinceMs: null,
    sessionElapsedMs: getSessionElapsedMs(state, nowPerformance),
    sessionSinceMs: null,
  };
}

export function hydrateRun(saved: RunState, nowPerformance: number): RunState {
  const migrated = migrateRun(saved);
  const sessionRuns = !['preparing', 'results', 'error'].includes(migrated.phase);
  return {
    ...migrated,
    activeSinceMs: migrated.phase === 'playing' ? nowPerformance : null,
    sessionSinceMs: sessionRuns ? nowPerformance : null,
  };
}

export function migrateRun(value: unknown): RunState {
  if (!isRunState(value)) throw new Error('Saved run is invalid or unsupported.');
  let migrated: RunState;
  if (value.schemaVersion === 1) {
    migrated = {
      ...value,
      schemaVersion: RUN_SCHEMA_VERSION,
      gameStates: {},
    } as RunState;
  } else if (value.schemaVersion !== RUN_SCHEMA_VERSION) {
    throw new Error(`Saved run version ${value.schemaVersion} is not supported.`);
  } else {
    migrated = value;
  }
  if (!isRunLedgerReconciled(migrated)) {
    throw new Error('Saved run has an inconsistent score ledger.');
  }
  return migrated;
}

function isRunState(value: unknown): value is RunState {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RunState>;
  const phases = ['preparing', 'splash', 'playing', 'outcome', 'loading', 'results', 'error'];
  const outcomes = ['solved', 'failed', 'skipped'];
  if (
    ![1, RUN_SCHEMA_VERSION].includes(candidate.schemaVersion ?? -1) ||
    typeof candidate.id !== 'string' ||
    candidate.id.length === 0 ||
    typeof candidate.seed !== 'string' ||
    !['practice', 'competitive'].includes(candidate.mode ?? '') ||
    !phases.includes(candidate.phase ?? '') ||
    !isFiniteNumber(candidate.phaseRevision) ||
    !isFiniteNumber(candidate.phaseStartedAt) ||
    !isFiniteNumber(candidate.createdAt) ||
    !isFiniteNumber(candidate.updatedAt) ||
    !Array.isArray(candidate.puzzles) ||
    !candidate.puzzles.every(isPuzzleEnvelope) ||
    !isFiniteNumber(candidate.currentIndex) ||
    candidate.currentIndex! < 0 ||
    !Number.isInteger(candidate.currentIndex) ||
    (candidate.puzzles.length > 0 && candidate.currentIndex! >= candidate.puzzles.length) ||
    (candidate.schemaVersion !== 1 && !isRecord(candidate.gameStates)) ||
    !Array.isArray(candidate.results) ||
    !candidate.results.every(
      (result) =>
        isRecord(result) &&
        GAME_IDS.includes(result.gameId as GameId) &&
        isFiniteNumber(result.rawTimeMs) &&
        result.rawTimeMs >= 0 &&
        isFiniteNumber(result.deltaMs) &&
        outcomes.includes(String(result.outcome)) &&
        typeof result.message === 'string' &&
        isRecord(result.metadata),
    ) ||
    candidate.results.length > candidate.puzzles.length ||
    !Array.isArray(candidate.scoreEvents) ||
    !candidate.scoreEvents.every(
      (event) =>
        isRecord(event) &&
        typeof event.id === 'string' &&
        event.id.length > 0 &&
        Number.isInteger(event.gameIndex) &&
        event.gameIndex >= 0 &&
        event.gameIndex < candidate.puzzles!.length &&
        isFiniteNumber(event.amountMs) &&
        typeof event.label === 'string' &&
        isFiniteNumber(event.createdAt),
    ) ||
    new Set(candidate.scoreEvents.map((event) => event.id)).size !== candidate.scoreEvents.length ||
    !isFiniteNumber(candidate.rawActiveMs) ||
    candidate.rawActiveMs! < 0 ||
    !(candidate.activeSinceMs === null || isFiniteNumber(candidate.activeSinceMs)) ||
    !isFiniteNumber(candidate.sessionElapsedMs) ||
    candidate.sessionElapsedMs! < 0 ||
    !(candidate.sessionSinceMs === null || isFiniteNumber(candidate.sessionSinceMs)) ||
    !isRecord(candidate.generationProgress) ||
    !isFiniteNumber(candidate.generationProgress.completed) ||
    !isFiniteNumber(candidate.generationProgress.total) ||
    typeof candidate.generationProgress.gameName !== 'string' ||
    !(candidate.error === null || typeof candidate.error === 'string')
  ) {
    return false;
  }
  return true;
}

function isPuzzleEnvelope(value: unknown): value is PuzzleEnvelope {
  if (!isRecord(value) || !GAME_IDS.includes(value.gameId as GameId)) return false;
  const generatorVersion = value.generatorVersion;
  const dataVersion = value.dataVersion;
  if (
    !Number.isInteger(value.seed) ||
    !Number.isInteger(generatorVersion) ||
    (generatorVersion as number) < 1 ||
    !Number.isInteger(dataVersion) ||
    (dataVersion as number) < 1
  ) {
    return false;
  }
  switch (value.gameId) {
    case 'wordy':
      return isWordyPuzzle(value.payload);
    case 'mini-crossword':
      return isMiniCrosswordPuzzle(value.payload);
    case 'clambers':
      return isClambersPuzzle(value.payload);
    case 'chromal':
      return isChromalPuzzle(value.payload);
    case 'ratiole':
      return isRatiolePuzzle(value.payload);
    case 'shapeup':
      return isShapeupPuzzle(value.payload);
    case 'mini-sudoku':
      return isMiniSudokuPuzzle(value.payload);
    case 'minesweeper':
      return isMinesweeperPuzzle(value.payload);
    case 'queens':
      return isQueensPuzzle(value.payload);
    case 'nonogram':
      return isNonogramPuzzle(value.payload);
    case 'paire':
      return isPairePuzzle(value.payload);
    default:
      return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
import { GAME_IDS, type GameId, type PuzzleEnvelope } from '../games/core/types';
import { isClambersPuzzle } from '../games/clambers/types';
import { isChromalPuzzle } from '../games/chromal/types';
import { isMinesweeperPuzzle } from '../games/minesweeper/types';
import { isMiniCrosswordPuzzle } from '../games/mini-crossword/types';
import { isMiniSudokuPuzzle } from '../games/mini-sudoku/types';
import { isNonogramPuzzle } from '../games/nonogram/types';
import { isPairePuzzle } from '../games/paire/types';
import { isQueensPuzzle } from '../games/queens/types';
import { isRatiolePuzzle } from '../games/ratiole/types';
import { isShapeupPuzzle } from '../games/shapeup/types';
import { isWordyPuzzle } from '../games/wordy/generate';
